/**
 * Reports API
 *
 * Sales analytics and reporting endpoints.
 */

import { apiClient } from './client';
import type {
  HourlySales,
  PaymentMethodSales,
  ReportDateRange,
  SalesSummary,
  StaffSales,
} from './types';

function buildQueryParams(params: ReportDateRange): string {
  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });
  if (params.locationId && params.locationId !== 'all') {
    queryParams.set('locationId', params.locationId);
  }
  if (params.channelIds && params.channelIds.length > 0) {
    queryParams.set('channelIds', params.channelIds.join(','));
  }
  return queryParams.toString();
}

export const reportsApi = {
  /**
   * Get sales summary for date range
   */
  async getSalesSummary(params: ReportDateRange): Promise<SalesSummary> {
    return apiClient.request(`/reports/sales-summary?${buildQueryParams(params)}`);
  },

  /**
   * Get sales breakdown by staff member
   */
  async getSalesByStaff(params: ReportDateRange): Promise<{ staffSales: StaffSales[] }> {
    return apiClient.request(`/reports/sales-by-staff?${buildQueryParams(params)}`);
  },

  /**
   * Get sales breakdown by hour of day
   */
  async getSalesByHour(params: ReportDateRange): Promise<{ hourlySales: HourlySales[] }> {
    return apiClient.request(`/reports/sales-by-hour?${buildQueryParams(params)}`);
  },

  /**
   * Get sales breakdown by payment method
   */
  async getSalesByPaymentMethod(params: ReportDateRange): Promise<{ paymentMethodSales: PaymentMethodSales[] }> {
    return apiClient.request(`/reports/sales-by-payment-method?${buildQueryParams(params)}`);
  },

  /**
   * Export report data as CSV
   */
  async exportReport(params: ReportDateRange & { type: 'summary' | 'orders' | 'staff' }): Promise<Blob> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
      type: params.type,
    });
    if (params.locationId && params.locationId !== 'all') {
      queryParams.set('locationId', params.locationId);
    }
    if (params.channelIds && params.channelIds.length > 0) {
      queryParams.set('channelIds', params.channelIds.join(','));
    }

    // For CSV export, we need to handle the response differently
    const response = await fetch(`${import.meta.env.VITE_API_URL}/reports/export?${queryParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${(apiClient as unknown as { sessionToken: string }).sessionToken}`,
        'X-Store-Hash': (apiClient as unknown as { storeHash: string }).storeHash,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export report');
    }

    return response.blob();
  },
};
